import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import type {
  Client,
  ClientListResponse,
  Organization,
  OrganizationListResponse,
  Project,
  ProjectListResponse,
  ProjectSummary,
  ProjectTemplate,
  ProjectTemplateListResponse,
} from '../types';

export default function TemplateLibraryPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [organizationId, setOrganizationId] = useState('');
  const [clients, setClients] = useState<Client[]>([]);
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [templates, setTemplates] = useState<ProjectTemplate[]>([]);
  const [sourceProjectId, setSourceProjectId] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [newProjectNames, setNewProjectNames] = useState<
    Record<string, string>
  >({});
  const [clientIds, setClientIds] = useState<Record<string, string>>({});
  const [editingTemplateId, setEditingTemplateId] = useState('');
  const [templateNames, setTemplateNames] = useState<Record<string, string>>(
    {}
  );
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [saving, setSaving] = useState(false);

  const setTemplateSharing = async (
    template: ProjectTemplate,
    isShared: boolean
  ) => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      await api.patch(`/api/templates/${template.id}/sharing`, {
        isShared,
      });
      setTemplates(values =>
        values.map(value =>
          value.id === template.id ? { ...value, isShared } : value
        )
      );
      setNotice(
        `“${template.name}” is now ${isShared ? 'shared with' : 'private to'} this workspace.`
      );
    } catch {
      setError('The template sharing setting could not be updated.');
    } finally {
      setSaving(false);
    }
  };

  const renameTemplate = async (template: ProjectTemplate) => {
    const name = templateNames[template.id]?.trim();
    if (!name) return;
    setSaving(true);
    setError('');
    setNotice('');
    try {
      await api.patch(`/api/templates/${template.id}`, { name });
      setTemplates(values =>
        values.map(value =>
          value.id === template.id ? { ...value, name } : value
        )
      );
      setEditingTemplateId('');
      setNotice(`Template renamed to “${name}”.`);
    } catch {
      setError('The template could not be renamed.');
    } finally {
      setSaving(false);
    }
  };

  const deleteTemplate = async (template: ProjectTemplate) => {
    if (!window.confirm(`Delete the “${template.name}” template?`)) return;
    setSaving(true);
    setError('');
    setNotice('');
    try {
      await api.delete(`/api/templates/${template.id}`);
      setTemplates(values => values.filter(value => value.id !== template.id));
      setNotice(`“${template.name}” was deleted.`);
    } catch {
      setError('The template could not be deleted.');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    api
      .get<OrganizationListResponse>('/api/organizations')
      .then(response => {
        setOrganizations(response.data.organizations);
        setOrganizationId(response.data.organizations[0]?.id ?? '');
      })
      .catch(() => setError(t('templates.errors.workspaces')));
  }, [t]);

  const load = async (id: string) => {
    const [templateResponse, projectResponse, clientResponse] =
      await Promise.all([
        api.get<ProjectTemplateListResponse>(
          `/api/organizations/${id}/templates`
        ),
        api.get<ProjectListResponse>(`/api/projects?organizationId=${id}`),
        api.get<ClientListResponse>(`/api/organizations/${id}/clients`),
      ]);
    setTemplates(templateResponse.data.templates);
    setProjects(projectResponse.data.projects);
    setClients(clientResponse.data.clients);
    setSourceProjectId(projectResponse.data.projects[0]?.id ?? '');
  };

  useEffect(() => {
    if (!organizationId) return;
    void load(organizationId).catch(() =>
      setError(t('templates.errors.library'))
    );
  }, [organizationId, t]);

  const saveTemplate = async () => {
    if (!sourceProjectId || !templateName.trim()) return;
    setSaving(true);
    setError('');
    setNotice('');
    try {
      await api.post(`/api/projects/${sourceProjectId}/templates`, {
        name: templateName,
        description: t('templates.savedDescription'),
        guidanceText: t('templates.savedGuidance'),
      });
      setTemplateName('');
      await load(organizationId);
      setNotice(t('templates.saved'));
    } catch {
      setError(t('templates.errors.save'));
    } finally {
      setSaving(false);
    }
  };

  const createProject = async (template: ProjectTemplate) => {
    const name = newProjectNames[template.id]?.trim();
    if (!name) return;
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const response = await api.post<Project>(
        `/api/templates/${template.id}/projects`,
        { name, clientId: clientIds[template.id] ?? '' }
      );
      navigate(`/projects/${response.data.id}`);
    } catch {
      setError(t('templates.errors.createProject'));
      setSaving(false);
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="page-intro">
        <div className="page-eyebrow">{t('templates.eyebrow')}</div>
        <h1 style={{ color: 'var(--text-primary)' }}>{t('templates.title')}</h1>
        <p style={{ color: 'var(--text-secondary)' }}>
          {t('templates.subtitle')}
        </p>
        <p style={{ color: 'var(--text-muted)', marginBottom: 0 }}>
          {templateContentScope}
        </p>
      </div>
      <section style={panel}>
        <label style={label}>
          {t('workspace.workspace')}
          <select
            value={organizationId}
            onChange={event => setOrganizationId(event.target.value)}
            style={input}
          >
            {organizations.map(item => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </label>
      </section>
      <section style={{ ...panel, marginTop: 16 }}>
        <h2 style={heading}>{t('templates.saveTitle')}</h2>
        <div style={grid}>
          <label style={label}>
            {t('templates.sourceProject')}
            <select
              value={sourceProjectId}
              onChange={event => setSourceProjectId(event.target.value)}
              style={input}
            >
              {projects.length === 0 && (
                <option value="">{t('templates.noSourceProjects')}</option>
              )}
              {projects.map(project => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </label>
          <label style={label}>
            {t('templates.templateName')}
            <input
              value={templateName}
              onChange={event => setTemplateName(event.target.value)}
              style={input}
            />
          </label>
          <button
            className="button button-primary"
            onClick={() => void saveTemplate()}
            disabled={saving || !sourceProjectId || !templateName.trim()}
          >
            {saving ? t('templates.saving') : t('templates.save')}
          </button>
        </div>
      </section>
      {notice && (
        <p role="status" className="status-message status-message-success">
          {notice}
        </p>
      )}
      {error && (
        <p role="alert" className="status-message status-message-error">
          {error}
        </p>
      )}
      <div style={{ display: 'grid', gap: 14, marginTop: 18 }}>
        {templates.length === 0 && (
          <section style={panel}>
            <h2 style={heading}>{t('templates.emptyTitle')}</h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: 0 }}>
              {projects.length === 0
                ? t('templates.emptyNoProjects')
                : t('templates.emptyWithProjects')}
            </p>
          </section>
        )}
        {templates.map(template => (
          <section key={template.id} style={panel}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 12,
                alignItems: 'flex-start',
              }}
            >
              <h2 style={heading}>{template.name}</h2>
              <span style={sharingStatus}>
                {template.isShared ? 'Shared with workspace' : 'Private to you'}
              </span>
            </div>
            <p style={{ color: 'var(--text-secondary)' }}>
              {template.description}
            </p>
            <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>
              {t('templates.summary', {
                checklist: template.checklistItems.length,
                evidence: template.evidenceCategories.length,
                tasks: template.taskPresets.length,
              })}
            </p>
            <details style={preview}>
              <summary
                style={{ cursor: 'pointer', color: 'var(--text-primary)' }}
              >
                Preview template contents
              </summary>
              <div style={{ display: 'grid', gap: 12, marginTop: 12 }}>
                <PreviewList
                  title="Checklist"
                  items={template.checklistItems.map(item =>
                    [item.requirementReference, item.title]
                      .filter(Boolean)
                      .join(' — ')
                  )}
                  empty="No checklist items"
                />
                <PreviewList
                  title="Evidence categories"
                  items={template.evidenceCategories}
                  empty="No evidence categories"
                />
                <PreviewList
                  title="Task presets"
                  items={template.taskPresets.map(task => task.title)}
                  empty="No task presets"
                />
                <PreviewList
                  title="Guidance"
                  items={template.guidanceText ? [template.guidanceText] : []}
                  empty="No guidance"
                />
              </div>
            </details>
            {template.canManage && (
              <div style={templateActions}>
                <button
                  className="button button-secondary"
                  onClick={() =>
                    void setTemplateSharing(template, !template.isShared)
                  }
                  disabled={saving}
                >
                  {templateSharingAction(template.isShared)}
                </button>
                <button
                  className="button button-secondary"
                  onClick={() => {
                    setEditingTemplateId(template.id);
                    setTemplateNames(values => ({
                      ...values,
                      [template.id]: template.name,
                    }));
                  }}
                  disabled={saving}
                >
                  Rename template
                </button>
                <button
                  className="button button-secondary"
                  onClick={() => void deleteTemplate(template)}
                  disabled={saving}
                >
                  Delete template
                </button>
              </div>
            )}
            {editingTemplateId === template.id && (
              <div style={renameForm}>
                <label style={label}>
                  Template name
                  <input
                    value={templateNames[template.id] ?? template.name}
                    onChange={event =>
                      setTemplateNames(values => ({
                        ...values,
                        [template.id]: event.target.value,
                      }))
                    }
                    style={input}
                  />
                </label>
                <div style={templateActions}>
                  <button
                    className="button button-primary"
                    onClick={() => void renameTemplate(template)}
                    disabled={saving || !templateNames[template.id]?.trim()}
                  >
                    Save name
                  </button>
                  <button
                    className="button button-secondary"
                    onClick={() => setEditingTemplateId('')}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
            <div style={startFromTemplate}>
              <h3 style={{ ...heading, fontSize: 16 }}>
                Start a project from this template
              </h3>
              <p style={{ color: 'var(--text-secondary)' }}>
                Choose a project name and an optional client. The new project
                starts with this template’s reusable structure.
              </p>
              <div style={grid}>
                <label style={label}>
                  {t('templates.newProjectName')}
                  <input
                    value={newProjectNames[template.id] ?? ''}
                    onChange={event =>
                      setNewProjectNames(values => ({
                        ...values,
                        [template.id]: event.target.value,
                      }))
                    }
                    style={input}
                  />
                </label>
                <label style={label}>
                  {t('workspace.client')}
                  <select
                    value={clientIds[template.id] ?? ''}
                    onChange={event =>
                      setClientIds(values => ({
                        ...values,
                        [template.id]: event.target.value,
                      }))
                    }
                    style={input}
                  >
                    <option value="">{t('workspace.noClient')}</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>
                        {client.name}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  className="button button-secondary"
                  onClick={() => void createProject(template)}
                  disabled={saving || !newProjectNames[template.id]?.trim()}
                >
                  Start project from template
                </button>
              </div>
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

export const templateContentScope =
  'Templates save reusable checklist items, evidence categories, task presets, and guidance. They never include documents, comments, findings, or decisions from the source project.';

// This pure label helper is covered by the page's focused presentation test.
// eslint-disable-next-line react-refresh/only-export-components
export function templateSharingAction(isShared: boolean) {
  return isShared ? 'Stop sharing with workspace' : 'Share with workspace';
}

function PreviewList({
  title,
  items,
  empty,
}: {
  title: string;
  items: string[];
  empty: string;
}) {
  return (
    <div>
      <strong style={{ color: 'var(--text-primary)', fontSize: 13 }}>
        {title}
      </strong>
      {items.length === 0 ? (
        <p style={{ color: 'var(--text-muted)', margin: '4px 0 0' }}>{empty}</p>
      ) : (
        <ul
          style={{
            color: 'var(--text-secondary)',
            margin: '4px 0 0',
            paddingLeft: 20,
          }}
        >
          {items.map((item, index) => (
            <li key={`${item}-${index}`}>{item}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

const panel: React.CSSProperties = {
  background: 'var(--bg-card)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-md)',
  padding: 18,
};
const heading: React.CSSProperties = {
  color: 'var(--text-primary)',
  fontSize: 18,
  marginTop: 0,
};
const grid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  alignItems: 'end',
  gap: 12,
};
const label: React.CSSProperties = {
  color: 'var(--text-secondary)',
  fontSize: 12,
};
const input: React.CSSProperties = {
  display: 'block',
  width: '100%',
  marginTop: 6,
  padding: 9,
  background: 'var(--bg-tertiary)',
  color: 'var(--text-primary)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)',
};
const sharingStatus: React.CSSProperties = {
  background: 'var(--bg-tertiary)',
  borderRadius: '999px',
  color: 'var(--text-secondary)',
  fontSize: 12,
  padding: '4px 8px',
  whiteSpace: 'nowrap',
};
const preview: React.CSSProperties = {
  borderTop: '1px solid var(--border)',
  borderBottom: '1px solid var(--border)',
  marginTop: 14,
  padding: '12px 0',
};
const startFromTemplate: React.CSSProperties = {
  marginTop: 18,
};
const templateActions: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
  marginTop: 14,
};
const renameForm: React.CSSProperties = {
  background: 'var(--bg-tertiary)',
  borderRadius: 'var(--radius-sm)',
  marginTop: 14,
  padding: 12,
};
